import React, { useEffect, useState } from 'react';
import { Button, Table } from 'reactstrap';
import i18n, {
  getDateFormatOptions,
  DateFormatOptionsForExport,
  DateFormatOptionsForFileNames,
} from '@/utils/i18n';
import { ItemDetailFragment } from '../../../graphql/fragments/item.generated';
import TrackableItemDisplay from '../../../components/TrackableItem/TrackableItemDisplay';
import { TrackableItem } from '../../../graphql/generatedTypes';
import { Link } from 'react-router-dom';
import { FaDownload } from 'react-icons/fa';
import { toCsvBlob } from '@/utils/data';
import { download, sanitizeFilename } from '@/utils/download';
import { useGetItemsAtLocationLazyQuery } from '../../../graphql/queries/getItemsAtLocation.generated';

interface AssetsTableProps {
  itemLocationId?: string | null;
  itemLocationName?: string;
}

const PAGE_SIZE = 20;

const AssetsTable: React.FC<AssetsTableProps> = ({
  itemLocationId,
  itemLocationName,
}) => {
  const [page, setPage] = useState(1);
  const [assets, setAssets] = useState<ItemDetailFragment[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [getAssets, { data, loading, error }] = useGetItemsAtLocationLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [getAllAssets] = useGetItemsAtLocationLazyQuery();

  // Initial load
  useEffect(() => {
    if (itemLocationId) {
      setIsInitialLoad(true);
      setAssets([]);
      setPage(1);
      setHasMore(true);
      getAssets({
        variables: {
          location_id: itemLocationId,
          filter: {
            item_type_category: 'ASSET',
          },
          page: 1,
          page_size: PAGE_SIZE,
        },
      });
    }
  }, [itemLocationId, getAssets]);

  // Handle initial data only
  useEffect(() => {
    if (data?.getItemsAtLocation && isInitialLoad) {
      setAssets(data.getItemsAtLocation);
      setHasMore(data.getItemsAtLocation.length === PAGE_SIZE);
      setPage(1);
      setIsInitialLoad(false);
    }
  }, [data, isInitialLoad]);

  const loadMore = async () => {
    if (!itemLocationId || loadingMore) return;

    setLoadingMore(true);
    try {
      const result = await getAssets({
        variables: {
          location_id: itemLocationId,
          filter: {
            item_type_category: 'ASSET',
          },
          page: page + 1,
          page_size: PAGE_SIZE,
        },
      });

      const newAssets = result.data?.getItemsAtLocation || [];
      setAssets((prevAssets) => [...prevAssets, ...newAssets]);
      setPage((prevPage) => prevPage + 1);
      setHasMore(newAssets.length === PAGE_SIZE);
    } catch (error) {
      console.error('Error loading more assets:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  async function downloadAllAssets() {
    setIsDownloading(true);
    try {
      const { data } = await getAllAssets({
        variables: {
          location_id: itemLocationId || '',
          filter: {
            item_type_category: 'ASSET',
          },
        },
      });

      if (data?.getItemsAtLocation) {
        const csvData = data.getItemsAtLocation.map((asset) => {
          const bookedAtInExportFormat = new Date(
            asset.arrival_date,
          ).toLocaleDateString(i18n.language, DateFormatOptionsForExport);

          return {
            asset_id: asset.id,
            asset_name: asset.name,
            item_type_name: asset.item_type.name,
            item_type_category: asset.item_type.category,
            booked_at: bookedAtInExportFormat,
            booked_by_user_id: asset.booked_by_user_id || '-',
            booked_by_user_name: asset.booked_by_user_name || '-',
          };
        });

        const csvBlob = toCsvBlob(csvData);
        const date = new Date()
          .toLocaleDateString(i18n.language, DateFormatOptionsForFileNames)
          .replaceAll('/', '-');
        const filename = `assets_in_${sanitizeFilename(
          itemLocationName || '',
        )}_${date}.csv`;

        download(csvBlob, filename);
      }
    } catch (error) {
      console.error('Error downloading assets:', error);
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <>
      <Table striped responsive>
        <thead>
          <tr>
            <th>{i18n.t('TrackableItems:name')}</th>
            <th>{i18n.t('TrackableItems:itemType')}</th>
            <th>{i18n.t('ItemLocationHistory:arrivalDate')}</th>
            <th>{i18n.t('ItemLocationHistory:bookedBy')}</th>
          </tr>
        </thead>
        <tbody>
          {assets.length === 0 && !loading ? (
            <tr>
              <td colSpan={4} className="text-center">
                {i18n.t('TrackableItems:noItems')}
              </td>
            </tr>
          ) : (
            assets.map((asset) => (
              <tr key={asset.id}>
                <td>
                  <TrackableItemDisplay
                    trackableItem={asset as unknown as TrackableItem}
                    showTrackableItemId={false}
                    size={'s'}
                  />
                </td>
                <td>{asset.item_type.name}</td>
                <td>
                  {new Date(asset.arrival_date).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(true),
                  )}
                </td>
                <td>
                  {asset.booked_by_user_id ? (
                    <Link to={`/users/${asset.booked_by_user_id}`}>
                      {asset.booked_by_user_name || asset.booked_by_user_id}
                    </Link>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {error && (
        <div className="alert alert-danger mt-2">
          {i18n.t('Common:error')}: {error.message}
        </div>
      )}

      {hasMore && (
        <Button
          className="ms-2 mt-2 mb-2"
          onClick={loadMore}
          disabled={loadingMore}
        >
          {loadingMore ? i18n.t('Common:loading') : i18n.t('Common:loadMore')}
        </Button>
      )}

      <Button
        className="float-end ms-2 mt-2 mb-2"
        onClick={downloadAllAssets}
        disabled={isDownloading || !assets.length}
      >
        {isDownloading ? (
          i18n.t('Common:loading')
        ) : (
          <>
            <FaDownload />
            {' ' + i18n.t('Common:download')}
          </>
        )}
      </Button>
    </>
  );
};

export default AssetsTable;
